import { useState, useEffect, useMemo } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getToken } from "@/pages/AdminLogin";
import { LogOut, Mail, RefreshCw, Users, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "In Discussion", "Closed"];
const STATUS_TONE = {
  New: "bg-[#d3a53a]/12 text-[#b4641c] border-[#d3a53a]/30",
  Contacted: "bg-[#1d3a1f]/8 text-[#1d3a1f] border-[#1d3a1f]/15",
  "In Discussion": "bg-[#d9a441]/15 text-[#8a6410] border-[#d9a441]/30",
  Closed: "bg-[#c0492f]/10 text-[#a23b26] border-[#c0492f]/20",
};

export default function AdminInbox() {
  const [leads, setLeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("inquiries");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const navigate = useNavigate();
  const token = getToken();

  const authAxios = useMemo(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = () => { localStorage.removeItem("farmsons_admin_token"); navigate("/admin/login"); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get("/leads");
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      toast.error("Could not load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const loadSubs = async () => {
    try {
      const { data } = await authAxios.get("/newsletter");
      setSubs(data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); return; }
      toast.error("Could not load subscribers");
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => { if (token && tab === "subscribers") loadSubs(); /* eslint-disable-next-line */ }, [tab]);

  if (!token) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await authAxios.patch(`/leads/${id}/status`, { status });
      toast.success(`Marked ${status}`);
    } catch {
      setLeads(prev);
      toast.error("Update failed");
    }
  };

  const visible = leads.filter((l) => filter === "All" || l.status === filter);
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: leads.filter((l) => l.status === s).length }), {});

  const tabBtn = (key, label, Icon, count) => (
    <button
      key={key}
      data-testid={`admin-tab-${key}`}
      onClick={() => setTab(key)}
      className={`inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full border transition-colors ${
        tab === key ? "bg-[#1d3a1f] text-[#fdfbf5] border-[#1d3a1f]" : "bg-white text-[#5f6b57] border-[#1d3a1f]/15 hover:border-[#d3a53a]"
      }`}
    >
      <Icon size={15} /> {label} <span className="opacity-70">{count}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <header className="bg-[#1d3a1f] text-[#fdfbf5]">
        <div className="max-w-[1280px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-lg">FARM<span className="text-[#d3a53a]">SONS</span> <span className="text-[#fdfbf5]/50 font-normal text-sm">Inquiry Inbox</span></Link>
          <div className="flex items-center gap-3">
            <button data-testid="inbox-refresh" onClick={tab === "inquiries" ? load : loadSubs} className="inline-flex items-center gap-1.5 text-sm text-[#fdfbf5]/80 hover:text-white"><RefreshCw size={15} /> Refresh</button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm bg-[#fdfbf5]/10 hover:bg-[#fdfbf5]/20 px-3 py-1.5 rounded-full"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {tabBtn("inquiries", "Inquiries", Inbox, leads.length)}
          {tabBtn("subscribers", "Subscribers", Users, subs.length)}
        </div>

        {tab === "subscribers" ? (
          subs.length === 0 ? (
            <p data-testid="subscribers-empty" className="text-[#5f6b57]">No newsletter subscribers yet.</p>
          ) : (
            <div className="space-y-3" data-testid="subscriber-list">
              {subs.map((s) => (
                <article key={s.id} data-testid={`subscriber-${s.id}`} className="bg-white border border-[#1d3a1f]/10 rounded-2xl px-6 py-4 card-soft flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <a href={`mailto:${s.email}`} className="inline-flex items-center gap-2 text-[#1d3a1f] font-medium hover:text-[#d3a53a]"><Mail size={15} /> {s.email}</a>
                  <span className="text-xs text-[#8a8570]">{new Date(s.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                </article>
              ))}
            </div>
          )
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {["All", ...STATUSES].map((s) => (
                <button key={s} data-testid={`inbox-filter-${s.replace(/\s/g, "-").toLowerCase()}`} onClick={() => setFilter(s)}
                  className={`text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${filter === s ? "bg-[#1d3a1f] text-[#fdfbf5] border-[#1d3a1f]" : "bg-white text-[#5f6b57] border-[#1d3a1f]/15 hover:border-[#d3a53a]"}`}>
                  {s}{s !== "All" && <span className="ml-1.5 opacity-70">{counts[s]}</span>}
                </button>
              ))}
              <span className="ml-auto text-sm text-[#5f6b57]">{leads.length} total inquiries</span>
            </div>

            {loading ? (
              <p className="text-[#5f6b57]">Loading inquiries…</p>
            ) : visible.length === 0 ? (
              <p data-testid="inbox-empty" className="text-[#5f6b57]">No inquiries in this view.</p>
            ) : (
              <div className="space-y-4" data-testid="inbox-list">
                {visible.map((l) => (
                  <article key={l.id} data-testid={`lead-${l.id}`} className="bg-white border border-[#1d3a1f]/10 rounded-2xl p-6 card-soft">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h2 className="font-display font-bold text-xl text-[#1d3a1f]">{l.name}</h2>
                          <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${STATUS_TONE[l.status]}`}>{l.status}</span>
                        </div>
                        <p className="mt-1 text-sm text-[#5f6b57]">{l.company} · {l.country} · <span className="text-[#b4641c] font-medium">{l.interest}</span></p>
                        <a href={`mailto:${l.email}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-[#1d3a1f] hover:text-[#d3a53a]"><Mail size={14} /> {l.email}</a>
                        <p className="mt-3 text-[#3f4a3c] leading-relaxed max-w-2xl">{l.message}</p>
                        <p className="mt-3 text-xs text-[#8a8570]">{new Date(l.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                      <div className="shrink-0">
                        <label className="block text-xs font-bold uppercase tracking-[0.12em] text-[#5f6b57] mb-1.5">Status</label>
                        <select data-testid={`status-select-${l.id}`} value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)}
                          className="border border-[#1d3a1f]/20 rounded-lg bg-white px-3 py-2 text-sm text-[#1d3a1f] focus:outline-none focus:border-[#d3a53a]">
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
